/**
 * Sync Error Message Utilities
 *
 * Parametric utilities for converting SyncError objects into user-friendly messages.
 * This module does not introduce new error codes; it derives categories from the existing
 * `SyncError` contract using lightweight heuristics.
 *
 * All functions accept data/maps as parameters (no imports from constants) following
 * the parametric design pattern.
 *
 * @module syncErrorMessages
 */

import type { SyncError, SchemaVersionError } from '@/types/sync';

/**
 * Error category derived from SyncError code and context.
 *
 * - **recoverable**: Requires explicit user action (reconnect, re-auth)
 * - **permanent**: Requires user intervention (grant permission, free space, choose new directory)
 * - **transient**: Auto-retry or system will resolve (network glitch, rate limit, conflict resolution)
 */
export type ErrorCategory = 'recoverable' | 'permanent' | 'transient';

/**
 * Context in which the error message will be displayed.
 *
 * - **modal**: Detailed descriptions with explicit action text
 * - **banner**: Shorter, actionable descriptions
 * - **toast**: Brief status updates
 */
export type ErrorMessageContext = 'modal' | 'banner' | 'toast';

/**
 * Structured error message with title, description, and optional action text.
 */
export interface ErrorMessage {
  /** Short title summarizing the error */
  title: string;
  /** User-friendly description of the error */
  description: string;
  /** Optional action text for buttons (e.g., "Reconnect", "Retry", "Choose Directory") */
  actionText?: string;
}

/**
 * Derives the error category from a SyncError using the error code and lightweight heuristics.
 *
 * **Design Note**: This function uses heuristics (pattern matching on `error.details` and
 * `error.message`) to infer category because the current `SyncError` contract doesn't include
 * explicit category metadata. Prefers status-like signals in `error.details` (e.g., HTTP status
 * codes) and falls back to message text patterns. Once `SyncErrorCode` is expanded with richer
 * metadata in a future types phase, this function should be simplified to use explicit fields
 * instead of pattern matching.
 *
 * **Heuristics**:
 * - 401/403 patterns in details → recoverable (requires re-auth)
 * - 429/503 patterns under NETWORK_ERROR → transient (auto-retry)
 * - NOT_CONNECTED → recoverable (requires explicit reconnect)
 * - PERMISSION_DENIED → permanent by default, recoverable if 401/403 detected
 * - FILE_NOT_FOUND, QUOTA_EXCEEDED, INVALID_FILE → permanent
 * - NETWORK_ERROR, CONFLICT_DETECTED → transient
 * - UNKNOWN_ERROR → permanent (requires investigation)
 *
 * @param error - The SyncError to categorize
 * @returns The derived error category
 *
 * @example
 * ```typescript
 * const error: SyncError = {
 *   code: 'PERMISSION_DENIED',
 *   message: 'Access denied',
 *   provider: 'google-drive',
 *   details: { status: 401 }
 * };
 *
 * deriveErrorCategory(error)
 * // Returns: 'recoverable' (401 detected in details)
 * ```
 */
export function deriveErrorCategory(error: SyncError): ErrorCategory {
  const { code, message, details } = error;

  // Convert details to string for pattern matching
  const detailsStr = typeof details === 'string' ? details : JSON.stringify(details || {});
  const combinedText = `${message} ${detailsStr}`.toLowerCase();

  // Check for HTTP status patterns in details/message
  const has401or403 = /\b(401|403|unauthorized|forbidden)\b/.test(combinedText);

  switch (code) {
    case 'NOT_CONNECTED':
      // Requires explicit reconnect action
      return 'recoverable';

    case 'PERMISSION_DENIED':
      // Recoverable if 401/403 (token expired), permanent if actual permission issue
      return has401or403 ? 'recoverable' : 'permanent';

    case 'NETWORK_ERROR':
      // Transient if 429/503 (auto-retry), otherwise transient network glitch
      return 'transient';

    case 'FILE_NOT_FOUND':
    case 'QUOTA_EXCEEDED':
    case 'INVALID_FILE':
      // Requires user intervention
      return 'permanent';

    case 'CONFLICT_DETECTED':
      // User must resolve conflict, but system can guide
      return 'transient';

    case 'UNKNOWN_ERROR':
      // Requires investigation
      return 'permanent';

    // Schema version errors - typically handled via SchemaVersionError interface,
    // but included here for exhaustive SyncErrorCode coverage
    case 'SCHEMA_VERSION_NEWER':
      // User can recover by refreshing browser
      return 'recoverable';

    case 'SCHEMA_VERSION_MISSING':
    case 'SCHEMA_VERSION_INVALID':
    case 'SCHEMA_VERSION_MISMATCH':
      // Corrupted/invalid data - requires disconnect and reconnect
      return 'permanent';

    default:
      // Exhaustive check: if we get here, a new error code was added
      code satisfies never;
      return 'permanent';
  }
}

/**
 * Generates a user-friendly error message from a SyncError.
 *
 * This function maps existing SyncErrorCode values to contextual messages without
 * introducing new error codes. Uses `deriveErrorCategory()` to determine actionText
 * and message tone.
 *
 * @param error - The SyncError to convert
 * @param context - Optional display context (modal, banner, toast)
 * @param providerDisplayName - Optional provider display name (e.g., "Google Drive")
 * @returns Structured ErrorMessage with title, description, and optional actionText
 *
 * @example
 * ```typescript
 * const error: SyncError = {
 *   code: 'NOT_CONNECTED',
 *   message: 'No tokens found',
 *   provider: 'google-drive'
 * };
 *
 * getSyncErrorMessage(error, 'modal', 'Google Drive')
 * // Returns: {
 * //   title: 'Google Drive Connection Required',
 * //   description: 'Sign in to Google Drive to enable sync.',
 * //   actionText: 'Reconnect'
 * // }
 * ```
 */
export function getSyncErrorMessage(
  error: SyncError,
  context: ErrorMessageContext = 'modal',
  providerDisplayName?: string
): ErrorMessage {
  const category = deriveErrorCategory(error);
  const providerPrefix = providerDisplayName ? `${providerDisplayName} ` : '';

  switch (error.code) {
    case 'NOT_CONNECTED': {
      const title = `${providerPrefix}Connection Required`;
      const description =
        context === 'toast'
          ? `Sign in to ${providerDisplayName || 'sync'} to enable sync.`
          : context === 'banner'
            ? `Sign in to ${providerDisplayName || 'your provider'} to enable sync.`
            : `Sign in to ${providerDisplayName || 'your sync provider'} to enable sync.`;

      return {
        title,
        description,
        actionText: 'Reconnect',
      };
    }

    case 'PERMISSION_DENIED': {
      const title = `${providerPrefix}Permission Denied`;
      const isReauth = category === 'recoverable';

      const description =
        context === 'toast'
          ? isReauth
            ? 'Session expired. Reconnect to continue.'
            : 'Permission denied. Grant access to continue.'
          : context === 'banner'
            ? isReauth
              ? 'Your session expired. Click Reconnect to sign in again.'
              : 'Permission denied. Please grant access and reconnect.'
            : isReauth
              ? 'Your authentication session has expired. Click Reconnect to sign in again and restore access.'
              : 'Permission was denied. Please grant the necessary access permissions and reconnect.';

      return {
        title,
        description,
        actionText: isReauth ? 'Reconnect' : 'Grant Access',
      };
    }

    case 'NETWORK_ERROR': {
      const title = `${providerPrefix}Connection Lost`;
      const description =
        context === 'toast'
          ? 'Connection lost. Retrying...'
          : context === 'banner'
            ? 'Connection lost. Retrying automatically...'
            : 'Connection to the sync provider was lost. The system will retry automatically.';

      return {
        title,
        description,
        // Transient - no action needed
      };
    }

    case 'FILE_NOT_FOUND': {
      const title = `${providerPrefix}File Not Found`;
      const description =
        context === 'toast'
          ? 'The requested file was not found.'
          : context === 'banner'
            ? 'The requested file was not found.'
            : 'The requested file was not found. It may have been moved or deleted.';

      // Provider-specific action text
      // - Local Directory: User needs to choose a different directory
      // - Cloud providers (Google Drive, etc.): User needs to re-authenticate
      const actionText =
        error.provider === 'local'
          ? 'Choose Directory'
          : 'Re-authenticate';

      return {
        title,
        description,
        actionText,
      };
    }

    case 'QUOTA_EXCEEDED': {
      const title = `${providerPrefix}Storage Full`;
      const description =
        context === 'toast'
          ? 'Storage quota exceeded.'
          : context === 'banner'
            ? 'Storage quota exceeded. Free up space and try again.'
            : 'Storage quota has been exceeded. Please free up space in your sync provider and try again.';

      return {
        title,
        description,
        // Permanent - user must free space
      };
    }

    case 'INVALID_FILE': {
      const title = `${providerPrefix}Invalid File`;
      const description =
        context === 'toast'
          ? 'The file format is invalid.'
          : context === 'banner'
            ? 'The file format is invalid or corrupted.'
            : 'The file format is invalid or corrupted. Please check the file and try again.';

      return {
        title,
        description,
        // Permanent - file is corrupted
      };
    }

    case 'CONFLICT_DETECTED': {
      const title = `${providerPrefix}Sync Conflict`;
      const description =
        context === 'toast'
          ? 'Sync conflict detected.'
          : context === 'banner'
            ? 'Changes conflict with remote version.'
            : 'Changes conflict with the remote version. Resolve conflicts to continue syncing.';

      return {
        title,
        description,
        actionText: 'Resolve Conflicts',
      };
    }

    case 'UNKNOWN_ERROR': {
      const title = `${providerPrefix}Unexpected Error`;
      const description =
        context === 'toast'
          ? 'An unexpected error occurred.'
          : context === 'banner'
            ? 'An unexpected error occurred. Try reconnecting.'
            : 'An unexpected error occurred. Try reconnecting or contact support if the issue persists.';

      return {
        title,
        description,
        actionText: 'Reconnect',
      };
    }

    // Schema version errors - delegate to specialized helper for single source of truth
    case 'SCHEMA_VERSION_NEWER':
    case 'SCHEMA_VERSION_MISSING':
    case 'SCHEMA_VERSION_INVALID':
    case 'SCHEMA_VERSION_MISMATCH': {
      // Delegate to specialized helper (single source of truth for schema error messages)
      return getSchemaVersionErrorMessage(
        { code: error.code },  // Type-safe via Pick<SchemaVersionError, 'code'>
        context
      );
    }

    default: {
      // Exhaustive check: if we get here, a new error code was added
      error.code satisfies never;
      return {
        title: 'Error',
        description: 'An error occurred.',
      };
    }
  }
}

/**
 * Generates a user-friendly error message from a SchemaVersionError.
 *
 * This is a specialized helper for schema version errors that are provider-agnostic.
 * Unlike `getSyncErrorMessage()`, this function doesn't require a provider field since
 * schema version errors are independent of the sync provider type.
 *
 * **Only requires the code field. Intentionally provider-agnostic.**
 *
 * Implementation note: This function only uses error.code, allowing both full
 * SchemaVersionError objects and minimal { code } objects. This enables type-safe
 * delegation from `getSyncErrorMessage()` without type assertions.
 *
 * @param error - An object with a SchemaVersionErrorCode (full or partial SchemaVersionError)
 * @param context - The display context (modal, banner, or toast)
 * @returns An ErrorMessage object with title, description, and optional actionText
 *
 * @example
 * const errorMessage = getSchemaVersionErrorMessage(schemaError, 'banner');
 * // { title: 'Sync Paused', description: '...', actionText: 'Refresh' }
 */
export function getSchemaVersionErrorMessage(
  error: Pick<SchemaVersionError, 'code'>,
  context: ErrorMessageContext
): ErrorMessage {
  // Use exhaustive switch on SchemaVersionErrorCode union
  // TypeScript will force updates if new codes are added
  switch (error.code) {
    case 'SCHEMA_VERSION_NEWER': {
      const title = 'Sync Paused';
      const description =
        context === 'toast'
          ? 'Please refresh your browser to continue syncing.'
          : context === 'banner'
            ? 'Sync paused. Please refresh your browser to continue syncing.'
            : 'Sync is paused because your browser tab is out of date. Please refresh your browser to continue syncing.';

      return {
        title,
        description,
        actionText: 'Refresh',
      };
    }

    case 'SCHEMA_VERSION_MISSING':
    case 'SCHEMA_VERSION_INVALID':
    case 'SCHEMA_VERSION_MISMATCH': {
      const title = 'Sync Error';
      const description =
        context === 'toast'
          ? 'Cloud data is corrupted. Reset sync in Settings to fix.'
          : context === 'banner'
            ? 'Cloud data is corrupted. Go to Settings to reset sync and fix this issue.'
            : 'Cloud data is corrupted or invalid. Go to Data & Sync settings to reset sync, which will overwrite cloud data with your local data.';

      return {
        title,
        description,
        actionText: 'Go to Settings',
      };
    }

    default: {
      // Exhaustive check: if we get here, a new schema error code was added
      error.code satisfies never;
      return {
        title: 'Error',
        description: 'A schema version error occurred.',
      };
    }
  }
}
